import androidx.compose.runtime.Composable
import androidx.compose.runtime.LaunchedEffect
import androidx.compose.runtime.getValue
import androidx.compose.runtime.mutableStateOf
import androidx.compose.runtime.remember
import androidx.compose.runtime.setValue
import kotlinx.browser.window
import org.jetbrains.compose.web.attributes.InputType
import org.jetbrains.compose.web.attributes.disabled
import org.jetbrains.compose.web.attributes.placeholder
import org.jetbrains.compose.web.dom.Button
import org.jetbrains.compose.web.dom.Div
import org.jetbrains.compose.web.dom.H3
import org.jetbrains.compose.web.dom.H4
import org.jetbrains.compose.web.dom.Input
import org.jetbrains.compose.web.dom.P
import org.jetbrains.compose.web.dom.Option
import org.jetbrains.compose.web.dom.Select
import org.jetbrains.compose.web.dom.Span
import org.jetbrains.compose.web.dom.Table
import org.jetbrains.compose.web.dom.Tbody
import org.jetbrains.compose.web.dom.Td
import org.jetbrains.compose.web.dom.Text
import org.jetbrains.compose.web.dom.Th
import org.jetbrains.compose.web.dom.Thead
import org.jetbrains.compose.web.dom.Tr

// Admin: participants, exercises and rating thresholds (SPECIFIKACE.md ch. 8 point 6).

/**
 * A static password, and only a screen door: it keeps the rest of the group from wandering into
 * the admin tabs by accident. It is not a security boundary -- it is visible in the JS bundle.
 * Real write permission is still RLS against the invite JWT (SPECIFIKACE.md ch. 7).
 */
private const val ADMIN_PASSWORD = "sgadmin"

private enum class AdminTab(val label: String) {
    Participants("Účastníci"),
    Exercises("Disciplíny"),
    Tiers("Prahy hodnocení"),
    Streaks("Streak a zmrazení"),
    Challenges("Výzvy"),
}

@Composable
fun AdminScreen(token: String) {
    var unlocked by remember { mutableStateOf(loadAdminUnlocked()) }
    if (!unlocked) {
        AdminLock(onUnlock = {
            unlocked = true
            saveAdminUnlocked(true)
        })
        return
    }

    var tab by remember { mutableStateOf(AdminTab.Participants) }
    Div {
        Div(attrs = { classes("tab-row") }) {
            AdminTab.entries.forEach { entry -> NavButton(entry.label, tab == entry) { tab = entry } }
        }
        when (tab) {
            AdminTab.Participants -> AdminParticipants(token)
            AdminTab.Exercises -> AdminExercises(token)
            AdminTab.Tiers -> AdminTiers(token)
            AdminTab.Streaks -> AdminStreakConfig(token)
            AdminTab.Challenges -> AdminChallenges(token)
        }
    }
}

@Composable
private fun AdminLock(onUnlock: () -> Unit) {
    var password by remember { mutableStateOf("") }
    var wrong by remember { mutableStateOf(false) }

    fun tryUnlock() {
        if (password == ADMIN_PASSWORD) onUnlock() else wrong = true
    }

    Div(attrs = { classes("admin-lock") }) {
        H3 { Text("Administrace je zamčená") }
        P { Text("Zadej heslo pro přístup ke správě účastníků, disciplín a prahů hodnocení.") }
        Div(attrs = { classes("input-row") }) {
            Input(type = InputType.Password, attrs = {
                placeholder("Heslo")
                value(password)
                onInput {
                    password = it.value
                    wrong = false
                }
                onKeyDown { if (it.key == "Enter") tryUnlock() }
            })
            PrimaryButton("Odemknout", onClick = ::tryUnlock)
        }
        if (wrong) P(attrs = { classes("error-text") }) { Text("Špatné heslo.") }
    }
}

/** A row action: runs the request, then reloads the list it belongs to. */
@Composable
private fun RowButton(label: String, state: AsyncState, action: suspend () -> Unit) {
    Button(attrs = {
        if (state.busy) disabled()
        onClick { state.launch(action) }
    }) { Text(label) }
}

@Composable
private fun AdminParticipants(token: String) {
    val state = rememberAsyncState()
    var participants by remember { mutableStateOf<List<Participant>>(emptyList()) }
    var newName by remember { mutableStateOf("") }

    suspend fun reload() {
        participants = Api.fetchParticipants(includeInactive = true)
    }

    LaunchedEffect(Unit) { state.run { reload() } }

    fun add() {
        val name = newName.trim()
        if (name.isBlank()) return
        state.launch {
            Api.createParticipant(token, name)
            newName = ""
            reload()
        }
    }

    ErrorMessage(state.error)
    Table {
        Thead { Tr { Th { Text("Jméno") }; Th { Text("Aktivní") }; Th {} } }
        Tbody {
            participants.forEach { participant ->
                Tr {
                    Td { Text(participant.name) }
                    Td { Text(if (participant.active) "ano" else "ne") }
                    Td {
                        RowButton(if (participant.active) "Deaktivovat" else "Aktivovat", state) {
                            Api.setParticipantActive(token, participant.id, !participant.active)
                            reload()
                        }
                    }
                }
            }
        }
    }
    Div(attrs = { classes("input-row") }) {
        TextField(newName, { newName = it }, placeholder = "Nové jméno…", onEnter = ::add)
        PrimaryButton("Přidat", enabled = newName.isNotBlank() && !state.busy, onClick = ::add)
    }
}

@Composable
private fun AdminExercises(token: String) {
    val state = rememberAsyncState()
    var types by remember { mutableStateOf<List<ExerciseType>>(emptyList()) }
    var newKey by remember { mutableStateOf("") }
    var newName by remember { mutableStateOf("") }
    var newUnit by remember { mutableStateOf("") }
    var newDivisor by remember { mutableStateOf("") }

    suspend fun reload() {
        types = Api.fetchExerciseTypes(includeInactive = true)
    }

    LaunchedEffect(Unit) { state.run { reload() } }

    ErrorMessage(state.error)
    P { Text("Norma = kolik jednotek se počítá jako 1 bod (u prkna/superman apod. bývá 1 -- zadává se rovnou počet).") }
    Table {
        Thead {
            Tr {
                Th { Text("Disciplína") }; Th { Text("Jednotka") }; Th { Text("Norma") }
                Th { Text("Aktivní") }; Th {}
            }
        }
        Tbody {
            types.forEach { type ->
                // Keyed on the id so an edit in progress survives a reload of the same row.
                var name by remember(type.id) { mutableStateOf(type.name) }
                var unit by remember(type.id) { mutableStateOf(type.unitLabel) }
                var divisor by remember(type.id) { mutableStateOf(formatNumber(type.pointsDivisor)) }
                Tr {
                    Td { TextField(name, { name = it }) }
                    Td { TextField(unit, { unit = it }) }
                    Td { TextField(divisor, { divisor = it }, decimal = true) }
                    Td { Text(if (type.active) "ano" else "ne") }
                    Td {
                        RowButton("Uložit", state) {
                            Api.updateExerciseType(
                                token,
                                type.copy(
                                    name = name,
                                    unitLabel = unit,
                                    pointsDivisor = divisor.toDoubleOrNull() ?: type.pointsDivisor,
                                ),
                            )
                            reload()
                        }
                        RowButton(if (type.active) "Deaktivovat" else "Aktivovat", state) {
                            Api.updateExerciseType(token, type.copy(active = !type.active))
                            reload()
                        }
                    }
                }
            }
        }
    }

    fun add() {
        val divisor = newDivisor.toDoubleOrNull() ?: return
        state.launch {
            Api.createExerciseType(
                token, newKey.trim(), newName.trim(), newUnit.trim(),
                divisor, (types.maxOfOrNull { it.sortOrder } ?: 0) + 1,
            )
            newKey = ""; newName = ""; newUnit = ""; newDivisor = ""
            reload()
        }
    }

    H4 { Text("Přidat disciplínu") }
    Div(attrs = { classes("input-row") }) {
        TextField(newKey, { newKey = it }, placeholder = "klíč, např. jumping_jacks")
        TextField(newName, { newName = it }, placeholder = "název")
        TextField(newUnit, { newUnit = it }, placeholder = "jednotka")
        TextField(newDivisor, { newDivisor = it }, placeholder = "norma", decimal = true)
        PrimaryButton(
            "Přidat",
            enabled = newKey.isNotBlank() && newName.isNotBlank() && newDivisor.toDoubleOrNull() != null && !state.busy,
            onClick = ::add,
        )
    }
}

@Composable
private fun AdminTiers(token: String) {
    val state = rememberAsyncState()
    var tiers by remember { mutableStateOf<List<RatingTier>>(emptyList()) }
    var newLabel by remember { mutableStateOf("") }
    var newMinPoints by remember { mutableStateOf("") }
    var newCountsAsCompleted by remember { mutableStateOf(true) }
    var newTracksStreak by remember { mutableStateOf(false) }

    suspend fun reload() {
        tiers = Api.fetchRatingTiers()
    }

    LaunchedEffect(Unit) { state.run { reload() } }

    ErrorMessage(state.error)
    P {
        Text(
            "Práh platí od zadaných bodů výš (do dalšího prahu). \"Splněný den\" řídí streak " +
                "a týdny bez vynechání (SPECIFIKACE.md kap. 5 bod 6). \"Vlastní streak\" přidá " +
                "prahu samostatný counter -- čtyři čísla vedle sebe už ale nikdo nečte.",
        )
    }
    Table {
        Thead {
            Tr {
                Th { Text("Štítek") }; Th { Text("Od bodů") }; Th { Text("Splněný den") }
                Th { Text("Vlastní streak") }; Th {}
            }
        }
        Tbody {
            tiers.forEach { tier ->
                // Keyed on the id so an edit in progress survives a reload of the same row.
                var label by remember(tier.id) { mutableStateOf(tier.label) }
                var minPoints by remember(tier.id) { mutableStateOf(formatNumber(tier.minPoints)) }
                var countsAsCompleted by remember(tier.id) { mutableStateOf(tier.countsAsCompleted) }
                var tracksStreak by remember(tier.id) { mutableStateOf(tier.tracksStreak) }
                Tr {
                    Td { TextField(label, { label = it }) }
                    Td { TextField(minPoints, { minPoints = it }, decimal = true) }
                    Td { ToggleButton(countsAsCompleted) { countsAsCompleted = !countsAsCompleted } }
                    Td { ToggleButton(tracksStreak) { tracksStreak = !tracksStreak } }
                    Td {
                        RowButton("Uložit", state) {
                            Api.updateRatingTier(
                                token,
                                tier.copy(
                                    label = label,
                                    minPoints = minPoints.toDoubleOrNull() ?: tier.minPoints,
                                    countsAsCompleted = countsAsCompleted,
                                    tracksStreak = tracksStreak,
                                ),
                            )
                            reload()
                        }
                        RowButton("Smazat", state) {
                            Api.deleteRatingTier(token, tier.id)
                            reload()
                        }
                    }
                }
            }
        }
    }

    fun add() {
        val minPoints = newMinPoints.toDoubleOrNull() ?: return
        state.launch {
            Api.createRatingTier(
                token, newLabel.trim(), minPoints, newCountsAsCompleted, newTracksStreak,
                (tiers.maxOfOrNull { it.sortOrder } ?: 0) + 1,
            )
            newLabel = ""; newMinPoints = ""; newCountsAsCompleted = true; newTracksStreak = false
            reload()
        }
    }

    H4 { Text("Přidat práh") }
    Div(attrs = { classes("input-row") }) {
        TextField(newLabel, { newLabel = it }, placeholder = "štítek")
        TextField(newMinPoints, { newMinPoints = it }, placeholder = "od bodů", decimal = true)
        ToggleButton(newCountsAsCompleted, prefix = "Splněný: ") { newCountsAsCompleted = !newCountsAsCompleted }
        ToggleButton(newTracksStreak, prefix = "Streak: ") { newTracksStreak = !newTracksStreak }
        PrimaryButton(
            "Přidat",
            enabled = newLabel.isNotBlank() && newMinPoints.toDoubleOrNull() != null && !state.busy,
            onClick = ::add,
        )
    }
}

/**
 * The freeze balance knobs (PLAN-STREAK.md ch. 3.2). Saving rebuilds the whole streak ledger by
 * trigger -- these numbers decide what every past day did, not just future ones -- so the warning
 * below is not decoration: turning the freeze off really does retroactively kill the streaks it
 * has been holding up.
 */
@Composable
private fun AdminStreakConfig(token: String) {
    val state = rememberAsyncState()
    var config by remember { mutableStateOf<StreakConfig?>(null) }
    var saved by remember { mutableStateOf(false) }

    LaunchedEffect(Unit) { state.run { config = Api.fetchStreakConfig() } }

    ErrorMessage(state.error)
    val current = config
    if (current == null) {
        if (state.loaded) P { Text("Konfiguraci se nepodařilo načíst.") }
        return
    }

    P {
        Text(
            "Zmrazení podrží řadu přes jeden vynechaný den. Nárok vzniká po zadaném počtu " +
                "odcvičených dní v kuse; při zapnutém doplácení musí následující den dosáhnout " +
                "zvolené úrovně, jinak řada přesto zanikne. Uložení přepočítá celou historii.",
        )
    }

    Div(attrs = { classes("config-form") }) {
        ConfigRow("Zmrazení zapnuté") {
            ToggleButton(current.freezeEnabled) { config = current.copy(freezeEnabled = !current.freezeEnabled) }
        }
        ConfigRow("Dní v kuse na jedno zmrazení") {
            NumberField(current.freezeEarnDays) { config = current.copy(freezeEarnDays = it) }
        }
        ConfigRow("Nejvíc naspořených zmrazení") {
            NumberField(current.freezeMax) { config = current.copy(freezeMax = it) }
        }
        ConfigRow("Vyžadovat doplacení") {
            ToggleButton(current.freezeRepayEnabled) {
                config = current.copy(freezeRepayEnabled = !current.freezeRepayEnabled)
            }
        }
        ConfigRow("Úroveň nutná k doplacení") {
            NumberField(current.freezeRepayMinLevel) { config = current.copy(freezeRepayMinLevel = it) }
            Span(attrs = { classes("field-note") }) { Text("1 = základní práh, 2 = druhý v pořadí…") }
        }
    }

    PrimaryButton(if (state.busy) "Ukládám…" else "Uložit", enabled = !state.busy) {
        state.launch {
            Api.updateStreakConfig(token, current)
            config = Api.fetchStreakConfig()
            saved = true
        }
    }
    if (saved) P(attrs = { classes("saved-text") }) { Text("Uloženo ✓ (historie přepočítána)") }
}

@Composable
private fun ConfigRow(label: String, control: @Composable () -> Unit) {
    Div(attrs = { classes("config-row") }) {
        Span(attrs = { classes("field-label") }) { Text(label) }
        control()
    }
}

/** Whole-number field that simply ignores anything that is not a number. */
@Composable
private fun NumberField(value: Int, onChange: (Int) -> Unit) {
    TextField(value.toString(), { it.toIntOrNull()?.let(onChange) }, cssClass = "config-input")
}

@Composable
private fun ToggleButton(value: Boolean, prefix: String = "", onToggle: () -> Unit) {
    Button(attrs = { onClick { onToggle() } }) { Text(prefix + if (value) "Ano" else "Ne") }
}

private enum class ChallengeMetric(val key: String, val label: String) {
    RatingDays("rating_days_count", "Počet dní na úrovni"),
    TotalPoints("total_points", "Celkové body"),
    ExercisePoints("exercise_points", "Body z disciplín"),
    DailyMinimum("daily_exercise_minimum", "Denní minimum"),
    Weekend("rating_on_weekdays", "Víkendový bojovník"),
}

@Composable
private fun AdminChallenges(token: String) {
    val state = rememberAsyncState()
    var challenges by remember { mutableStateOf<List<Challenge>>(emptyList()) }
    var exercises by remember { mutableStateOf<List<ExerciseType>>(emptyList()) }
    var tiers by remember { mutableStateOf<List<RatingTier>>(emptyList()) }
    var name by remember { mutableStateOf("") }
    var description by remember { mutableStateOf("") }
    var periodKind by remember { mutableStateOf("week") }
    var anchorDate by remember { mutableStateOf(todayIso()) }
    var metric by remember { mutableStateOf(ChallengeMetric.RatingDays) }
    var selectedExercises by remember { mutableStateOf<Set<String>>(emptySet()) }
    var ratingTierId by remember { mutableStateOf("") }
    var dailyPoints by remember { mutableStateOf("4") }
    var bronze by remember { mutableStateOf("1") }
    var silver by remember { mutableStateOf("2") }
    var gold by remember { mutableStateOf("4") }
    var medalTierIds by remember { mutableStateOf<Map<String, String>>(emptyMap()) }

    suspend fun reload() {
        challenges = Api.fetchChallenges(token)
        exercises = Api.fetchExerciseTypes(includeInactive = true)
        tiers = Api.fetchRatingTiers()
        if (ratingTierId.isBlank()) {
            ratingTierId = tiers.firstOrNull { it.label.contains("ARCIBOREC", true) }?.id
                ?: tiers.lastOrNull()?.id.orEmpty()
        }
        if (medalTierIds.isEmpty()) {
            fun tierId(label: String, fallback: Int): String =
                tiers.firstOrNull { it.label.equals(label, true) }?.id
                    ?: tiers.getOrNull(fallback)?.id.orEmpty()
            medalTierIds = mapOf(
                "bronze" to tierId("BOREC", 0),
                "silver" to tierId("SUPERBOREC", 1),
                "gold" to tierId("ARCIBOREC", 2),
            )
        }
    }

    LaunchedEffect(Unit) { state.run { reload() } }

    val startsOn = if (periodKind == "week") startOfWeekIso(anchorDate) else startOfMonthIso(anchorDate)
    val endsOn = if (periodKind == "week") endOfWeekIso(startsOn) else endOfMonthIso(startsOn)
    val numericTargets = listOfNotNull(
        bronze.toDoubleOrNull()?.let { "bronze" to it },
        silver.toDoubleOrNull()?.let { "silver" to it },
        gold.toDoubleOrNull()?.let { "gold" to it },
    ).toMap()
    val targetsOrdered = listOf("bronze", "silver", "gold").mapNotNull(numericTargets::get)
    val tierPoints = tiers.associate { it.id to it.minPoints }
    val weekendTargets = listOf("bronze", "silver", "gold")
        .mapNotNull { medalTierIds[it]?.takeIf(String::isNotBlank)?.let(tierPoints::get) }
    val targetsValid = if (metric == ChallengeMetric.Weekend) {
        weekendTargets.isNotEmpty() && weekendTargets.zipWithNext().all { it.first < it.second }
    } else {
        targetsOrdered.isNotEmpty() && targetsOrdered.all { it > 0 } &&
            targetsOrdered.zipWithNext().all { it.first < it.second }
    }
    val canCreate = name.isNotBlank() && !state.busy && targetsValid &&
        (metric != ChallengeMetric.ExercisePoints || selectedExercises.isNotEmpty()) &&
        (metric != ChallengeMetric.DailyMinimum || (dailyPoints.toDoubleOrNull() ?: 0.0) > 0) &&
        (metric != ChallengeMetric.RatingDays || ratingTierId.isNotBlank()) &&
        (metric != ChallengeMetric.Weekend || weekendTargets.isNotEmpty())

    fun create() {
        if (!canCreate) return
        state.launch {
            Api.createChallenge(
                token,
                ChallengeDraft(
                    name = name.trim(),
                    description = description.trim(),
                    startsOn = startsOn,
                    endsOn = endsOn,
                    periodKind = periodKind,
                    metricKind = metric.key,
                    exerciseTypeIds = selectedExercises.toList(),
                    ratingTierId = ratingTierId.takeIf { metric == ChallengeMetric.RatingDays },
                    dailyPoints = dailyPoints.toDoubleOrNull().takeIf { metric == ChallengeMetric.DailyMinimum },
                    medalTargets = if (metric == ChallengeMetric.Weekend) emptyMap() else numericTargets,
                    medalTierIds = if (metric == ChallengeMetric.Weekend) medalTierIds.filterValues { it.isNotBlank() } else emptyMap(),
                ),
            )
            name = ""
            description = ""
            reload()
        }
    }

    ErrorMessage(state.error)
    H3 { Text("Nová výzva") }
    Div(attrs = { classes("challenge-admin-form") }) {
        ConfigRow("Název") { TextField(name, { name = it }, placeholder = "Např. Arciborecký týden") }
        ConfigRow("Popis") { TextField(description, { description = it }, placeholder = "Krátké vysvětlení výzvy") }
        ConfigRow("Období") {
            Div(attrs = { classes("challenge-period-control") }) {
                Div(attrs = { classes("tab-row", "tab-row-inline") }) {
                    NavButton("Týden", periodKind == "week") { periodKind = "week" }
                    NavButton("Měsíc", periodKind == "month") {
                        periodKind = "month"
                        if (metric == ChallengeMetric.Weekend) metric = ChallengeMetric.RatingDays
                    }
                }
                Input(type = InputType.Date, attrs = {
                    value(anchorDate)
                    onInput { if (it.value.isNotBlank()) anchorDate = it.value }
                })
                Span(attrs = { classes("field-note") }) { Text(formatCzechDateRange(startsOn, endsOn)) }
            }
        }
        ConfigRow("Typ") {
            Div(attrs = { classes("challenge-type-grid") }) {
                ChallengeMetric.entries.filter { periodKind == "week" || it != ChallengeMetric.Weekend }.forEach { option ->
                    NavButton(option.label, metric == option) { metric = option }
                }
            }
        }

        if (metric == ChallengeMetric.RatingDays) {
            ConfigRow("Požadovaná úroveň") {
                TierSelect(tiers, ratingTierId) { ratingTierId = it }
            }
        }
        if (metric == ChallengeMetric.DailyMinimum) {
            ConfigRow("Denní minimum bodů") {
                TextField(dailyPoints, { dailyPoints = it }, decimal = true, cssClass = "config-input")
            }
        }
        if (metric in listOf(ChallengeMetric.RatingDays, ChallengeMetric.ExercisePoints,
                ChallengeMetric.DailyMinimum, ChallengeMetric.Weekend)) {
            ConfigRow(if (metric == ChallengeMetric.ExercisePoints) "Disciplíny" else "Omezit na disciplíny") {
                Div(attrs = { classes("challenge-exercises") }) {
                    exercises.forEach { exercise ->
                        val chosen = exercise.id in selectedExercises
                        Button(attrs = {
                            if (chosen) classes("active")
                            onClick {
                                selectedExercises = if (chosen) selectedExercises - exercise.id else selectedExercises + exercise.id
                            }
                        }) { Text(exercise.name) }
                    }
                }
                Span(attrs = { classes("field-note") }) {
                    Text(if (selectedExercises.isEmpty()) "Bez výběru se počítají všechny disciplíny." else "Počítají se jen označené disciplíny.")
                }
            }
        }

        H4 { Text("Stupně medailí") }
        if (metric == ChallengeMetric.Weekend) {
            MedalTierRow("🥉 Bronz", "bronze", tiers, medalTierIds) { medalTierIds = medalTierIds + ("bronze" to it) }
            MedalTierRow("🥈 Stříbro", "silver", tiers, medalTierIds) { medalTierIds = medalTierIds + ("silver" to it) }
            MedalTierRow("🥇 Zlato", "gold", tiers, medalTierIds) { medalTierIds = medalTierIds + ("gold" to it) }
            if (!targetsValid) P(attrs = { classes("error-text") }) {
                Text("Vyšší medaile musí vyžadovat vyšší úroveň; volba Vypnuto daný stupeň odstraní.")
            }
        } else {
            ConfigRow("🥉 Bronz") { TextField(bronze, { bronze = it }, placeholder = "vypnuto", decimal = true) }
            ConfigRow("🥈 Stříbro") { TextField(silver, { silver = it }, placeholder = "vypnuto", decimal = true) }
            ConfigRow("🥇 Zlato") { TextField(gold, { gold = it }, placeholder = "vypnuto", decimal = true) }
            if (!targetsValid) P(attrs = { classes("error-text") }) {
                Text("Vyšší medaile musí mít vyšší kladný cíl; prázdná hodnota stupeň vypne.")
            }
        }
        P(attrs = { classes("field-note") }) {
            Text(if (periodKind == "week") "Automatické vyhodnocení: hned po skončení neděle."
            else "Automatické vyhodnocení: po jednom celém dni na doplnění zápisů.")
        }
        PrimaryButton(if (state.busy) "Vytvářím…" else "Vytvořit a zveřejnit", enabled = canCreate, onClick = ::create)
    }

    H3 { Text("Nastavené výzvy") }
    if (!state.loaded) LoadingText() else if (challenges.isEmpty()) P { Text("Zatím žádná výzva.") } else Table {
        Thead { Tr { Th { Text("Výzva") }; Th { Text("Období") }; Th { Text("Stav") }; Th {} } }
        Tbody {
            challenges.forEach { challenge ->
                var editedName by remember(challenge.id) { mutableStateOf(challenge.name) }
                var editedDescription by remember(challenge.id) { mutableStateOf(challenge.description) }
                Tr {
                    Td {
                        TextField(editedName, { editedName = it })
                        TextField(editedDescription, { editedDescription = it }, placeholder = "popis")
                    }
                    Td { Text(formatCzechDateRange(challenge.startsOn, challenge.endsOn)) }
                    Td { Text(when (challenge.status) { "published" -> "aktivní"; "finished" -> "dokončená"; else -> "koncept" }) }
                    Td {
                        RowButton("Uložit text", state) {
                            Api.updateChallengeText(token, challenge.id, editedName.trim(), editedDescription.trim())
                            reload()
                        }
                        if (challenge.status == "published") Button(attrs = {
                            if (state.busy) disabled()
                            onClick {
                                if (window.confirm("Opravdu výzvu předčasně uzavřít? Výsledky se uloží jako konečné.")) {
                                    state.launch {
                                        Api.finishChallenge(token, challenge.id)
                                        reload()
                                    }
                                }
                            }
                        }) { Text("Vyhodnotit nyní") }
                    }
                }
            }
        }
    }
}

@Composable
private fun TierSelect(
    tiers: List<RatingTier>,
    selectedId: String,
    allowNone: Boolean = false,
    onSelect: (String) -> Unit,
) {
    Select(attrs = { onChange { onSelect(it.value ?: "") } }) {
        if (allowNone) Option(value = "", attrs = { if (selectedId.isBlank()) attr("selected", "selected") }) {
            Text("Vypnuto")
        }
        tiers.forEach { tier ->
            Option(value = tier.id, attrs = { if (tier.id == selectedId) attr("selected", "selected") }) {
                Text("${tier.label} (${formatNumber(tier.minPoints)} b.)")
            }
        }
    }
}

@Composable
private fun MedalTierRow(
    label: String,
    medal: String,
    tiers: List<RatingTier>,
    selections: Map<String, String>,
    onSelect: (String) -> Unit,
) {
    ConfigRow(label) { TierSelect(tiers, selections[medal].orEmpty(), allowNone = true, onSelect = onSelect) }
}
